import { parseGeneratedQuestions, type GeneratedQuestion } from "@zenforge/shared";
import { Notice } from "./components.js";

/**
 * Renders generated questions as questions, rather than as the raw JSON they
 * arrive in.
 *
 * A reviewer approving these is deciding whether each question is correct,
 * answerable, and marked with the right answer — none of which is legible in
 * a pretty-printed array of objects, where `isCorrect: true` sits at the end
 * of the third nested field of the second option. The whole purpose of this
 * screen is that judgement, so the shape it presents has to match it.
 *
 * Fails visibly rather than silently: content that does not parse gets the
 * same loud notice the raw JSON preview gives, because a Book In Questions
 * category whose output is not valid questions cannot be published and the
 * reviewer needs to know that before clicking Approve.
 */
export function QuestionPreview({ content }: { content: string }) {
  const { questions, error } = parseGeneratedQuestions(content);

  if (error) {
    return (
      <>
        <Notice kind="error">{error}</Notice>
        <pre className="preview">{content}</pre>
      </>
    );
  }

  return (
    <div className="preview">
      <p className="mini muted">
        {questions.length} question{questions.length === 1 ? "" : "s"}
      </p>
      {questions.map((question, index) => (
        <QuestionCard key={index} question={question} index={index} />
      ))}
    </div>
  );
}

/** Answer may be a string or an array (match-the-following) — both render as
 *  one line, since the distinction matters to the API and not to a reader. */
function answerText(answer: GeneratedQuestion["answer"]): string | null {
  if (Array.isArray(answer)) return answer.length > 0 ? answer.join(", ") : null;
  if (typeof answer === "string" && answer.trim()) return answer;
  return null;
}

function QuestionCard({ question, index }: { question: GeneratedQuestion; index: number }) {
  const options = question.options ?? [];
  const columnA = question.columnA ?? [];
  const columnB = question.columnB ?? [];
  const answer = answerText(question.answer);

  return (
    <section style={{ marginBottom: "1.5rem", paddingBottom: "1rem", borderBottom: "1px solid #eee" }}>
      <p style={{ fontWeight: 600, marginBottom: "0.5rem" }}>
        {question.questionNo ?? index + 1}. {question.question}
      </p>

      {options.length > 0 && (
        <ul style={{ listStyle: "none", paddingLeft: 0, margin: "0 0 0.5rem" }}>
          {options.map((option, optionIndex) => (
            <li key={optionIndex} style={{ marginBottom: "0.25rem" }}>
              {/* The marker, not colour alone — this has to be readable in a
                  printout and by anyone who does not distinguish red/green. */}
              <span style={{ fontWeight: option.isCorrect ? 600 : 400 }}>
                {option.isCorrect ? "✓ " : "   "}
                {String.fromCharCode(65 + optionIndex)}. {option.optionText}
              </span>
              {option.explanation && (
                <span className="mini muted"> — {option.explanation}</span>
              )}
            </li>
          ))}
        </ul>
      )}

      {columnA.length > 0 && (
        <table style={{ marginBottom: "0.5rem" }}>
          <tbody>
            {columnA.map((left, rowIndex) => (
              <tr key={rowIndex}>
                <td style={{ paddingRight: "1.5rem" }}>{left}</td>
                <td>{columnB[rowIndex] ?? ""}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {answer && (
        <p className="mini" style={{ margin: "0 0 0.25rem" }}>
          <strong>Answer:</strong> {answer}
        </p>
      )}

      {question.explanation && (
        <p className="mini muted" style={{ margin: 0 }}>
          <strong>Explanation:</strong> {question.explanation}
        </p>
      )}

      {(question.variants ?? []).length > 0 && (
        <p className="mini muted" style={{ margin: "0.25rem 0 0" }}>
          <strong>Variants:</strong> {(question.variants ?? []).join(" · ")}
        </p>
      )}
    </section>
  );
}
